import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import { API_URL } from "../config";
import { clearStoredAuth } from "../utils";
import { fetchJson, parseErrorMessage, type HttpError } from "../utils/fetchJson";

const isUnauthorizedError = (error: Error) =>
  (error as HttpError)?.status === 401 || error.message === "401";

const redirectUnauthorized = (navigate: ReturnType<typeof useNavigate>, error: Error) => {
  if (!isUnauthorizedError(error)) {
    return;
  }
  clearStoredAuth();
  navigate("/");
};

export type LibraryFolderLink = {
  id?: number;
  operatorRef?: number;
  folderRelativePath: string;
  kind: "registration" | "icao_type";
  value: string;
};

export type LibraryTreeResponse = FilesTreeResponse & {
  folderLinks?: LibraryFolderLink[];
};

export type LibraryScanDiff = {
  operatorRef: number;
  scannedAt: string;
  added: string[];
  modified: string[];
  removed: string[];
  addedCount: number;
  modifiedCount: number;
  removedCount: number;
};

export type LibraryComplianceResponse = {
  operatorRef: number;
  generatedAt: string;
  summary: {
    userCount: number;
    fileCount: number;
    folderCount: number;
    unreadDocumentCount: number;
    oldestUnreadAt: string | null;
    overallReadPercent: number;
    usersWithReport: number;
    indexedFileCount: number;
  };
  users: Array<{
    userId: number;
    tlc: string | null;
    email: string | null;
    reportDate: string | null;
    readCount: number;
    notReadCount: number;
    notReceivedCount: number;
    totalFiles: number;
    readPercent: number;
    receivedPercent: number;
  }>;
  files: Array<{
    fileId: number;
    relativePath: string;
    displayName: string;
    createdAt: string | null;
    lastModified: string | null;
    sizeBytes: number | null;
    readBy: number;
    notReadBy: number;
    notReceivedBy: number;
    readPercent: number;
    receivedPercent: number;
    notReadUsers: Array<{ userId: number; tlc: string | null }>;
    notReceivedUsers: Array<{ userId: number; tlc: string | null }>;
  }>;
  folders: Array<{
    path: string;
    name: string;
    documentCount: number;
    unreadDocumentCount: number;
    oldestUnreadAt: string | null;
    lastModified: string | null;
    readPercent: number;
  }>;
};

export type LibraryAircraftOptions = {
  operatorRef: number;
  registrations: string[];
  types: string[];
  aircraft: Array<{
    registration: string;
    type?: string | null;
    listGroup?: string;
  }>;
};

const normalizeOperatorId = (operatorId?: number | null) => {
  const parsed = Number.parseInt(`${operatorId ?? ""}`, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const withOperatorParam = (searchParams: URLSearchParams, operatorId?: number | null) => {
  const normalized = normalizeOperatorId(operatorId);
  if (normalized) {
    searchParams.set("operatorId", `${normalized}`);
  }
  return searchParams;
};

const withOperatorBody = <T extends Record<string, unknown>>(
  body: T,
  operatorId?: number | null,
): T & { operatorId?: number } => {
  const normalized = normalizeOperatorId(operatorId);
  return normalized ? { ...body, operatorId: normalized } : body;
};

const operatorQueryKey = (operatorId?: number | null) =>
  normalizeOperatorId(operatorId) ?? "session";

export const buildLibraryAircraftOptionsUrl = (operatorId?: number | null) => {
  const searchParams = withOperatorParam(new URLSearchParams(), operatorId);
  return `${API_URL}/library/aircraft-options${
    searchParams.toString() ? `?${searchParams.toString()}` : ""
  }`;
};

const fetchLibraryTree = async (
  path?: string | null,
  operatorId?: number | null,
): Promise<LibraryTreeResponse> => {
  const searchParams = withOperatorParam(new URLSearchParams(), operatorId);
  if (path) {
    searchParams.set("path", path);
  }
  return fetchJson<LibraryTreeResponse>(
    `${API_URL}/library/tree${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
  );
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const parentRelativePath = (relativePath: string) => {
  const normalized = `${relativePath || ""}`.replace(/^\/+|\/+$/g, "");
  if (!normalized.includes("/")) {
    return "";
  }
  return normalized.slice(0, normalized.lastIndexOf("/"));
};

const basenameRelativePath = (relativePath: string) => {
  const normalized = `${relativePath || ""}`.replace(/^\/+|\/+$/g, "");
  return normalized.includes("/")
    ? normalized.slice(normalized.lastIndexOf("/") + 1)
    : normalized;
};

const libraryTreeQueryKey = (operatorId: number | null | undefined, treePath: string) =>
  ["library-tree", operatorQueryKey(operatorId), treePath] as const;

/**
 * Tree API looks up children by parent_dav_path. Resolve a relative path's
 * parent DAV path from cached tree nodes (never pass relative paths as ?path=).
 */
const resolveParentDavPathFromCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  relativePath: string,
  operatorId?: number | null,
): string | null => {
  const parentRel = parentRelativePath(relativePath);
  if (!parentRel) {
    return "";
  }

  const opKey = operatorQueryKey(operatorId);
  const cached = queryClient.getQueriesData<LibraryTreeResponse>(["library-tree", opKey]);

  for (const [, data] of cached) {
    const parentNode = (data?.nodes || []).find((node) => node.relativePath === parentRel);
    if (parentNode?.path) {
      return parentNode.path;
    }
  }

  // Parent listing query key is the DAV path of the parent folder.
  for (const [key, data] of cached) {
    if (!(data?.nodes || []).some((node) => node.relativePath === relativePath)) {
      continue;
    }
    const treePath = Array.isArray(key) ? `${key[2] ?? ""}` : "";
    return treePath;
  }

  return null;
};

const findNodeInTreeCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  relativePath: string,
  operatorId?: number | null,
): FileExplorerNode | null => {
  const opKey = operatorQueryKey(operatorId);
  const cached = queryClient.getQueriesData<LibraryTreeResponse>(["library-tree", opKey]);
  for (const [, data] of cached) {
    const match = (data?.nodes || []).find((node) => node.relativePath === relativePath);
    if (match) {
      return match;
    }
  }
  return null;
};

const patchTreeCachesForRelativePath = (
  queryClient: ReturnType<typeof useQueryClient>,
  relativePath: string,
  operatorId: number | null | undefined,
  updater: (nodes: FileExplorerNode[]) => FileExplorerNode[],
) => {
  const opKey = operatorQueryKey(operatorId);
  const cached = queryClient.getQueriesData<LibraryTreeResponse>(["library-tree", opKey]);
  for (const [key, data] of cached) {
    if (!data?.nodes?.length) {
      continue;
    }
    const hasMatch = data.nodes.some(
      (node) =>
        node.relativePath === relativePath ||
        `${node.relativePath || ""}`.startsWith(`${relativePath}/`),
    );
    if (!hasMatch) {
      continue;
    }
    queryClient.setQueryData<LibraryTreeResponse>(key, {
      ...data,
      nodes: updater(data.nodes),
    });
  }
};

/**
 * Library mutations queue a background files-index refresh. The explorer reads
 * that index, so refetching immediately still shows the pre-mutation tree.
 * Poll until the refresh settles (or an expected path appears), then invalidate.
 */
const waitForLibraryIndexIdle = async ({
  queryClient,
  operatorId = null,
  initialDelayMs = 250,
  intervalMs = 500,
  maxAttempts = 40,
  expectRelativePath = null,
  expectAbsentPath = null,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  operatorId?: number | null;
  initialDelayMs?: number;
  intervalMs?: number;
  maxAttempts?: number;
  expectRelativePath?: string | null;
  expectAbsentPath?: string | null;
}) => {
  await sleep(initialDelayMs);
  let sawRefreshing = false;

  const targetRelative = expectRelativePath || expectAbsentPath;
  const parentDavPath = targetRelative
    ? resolveParentDavPathFromCache(queryClient, targetRelative, operatorId)
    : "";

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      // Prefer a precise parent listing when we know the DAV path; otherwise
      // only watch the root refresh flag (avoids hammering with relative paths).
      const treePathForPoll =
        parentDavPath === null ? null : parentDavPath === "" ? null : parentDavPath;
      const tree =
        treePathForPoll === null && parentDavPath === null
          ? await fetchLibraryTree(null, operatorId)
          : await fetchLibraryTree(treePathForPoll, operatorId);

      if (tree.isRefreshing) {
        sawRefreshing = true;
        await sleep(intervalMs);
        continue;
      }

      if (expectRelativePath && parentDavPath !== null) {
        const found = (tree.nodes || []).some(
          (node) =>
            node.relativePath === expectRelativePath ||
            node.name === basenameRelativePath(expectRelativePath),
        );
        if (found) {
          return tree;
        }
      } else if (expectAbsentPath && parentDavPath !== null) {
        const stillPresent = (tree.nodes || []).some(
          (node) => node.relativePath === expectAbsentPath,
        );
        if (!stillPresent) {
          return tree;
        }
      } else if (!expectRelativePath && !expectAbsentPath) {
        if (!sawRefreshing && attempt < 2) {
          await sleep(intervalMs);
          continue;
        }
        return tree;
      } else if (!sawRefreshing && attempt < 2) {
        // Parent DAV unknown: give the refresh a moment, then settle.
        await sleep(intervalMs);
        continue;
      } else if (sawRefreshing || attempt >= 2) {
        return tree;
      }
    } catch (_error) {
      // keep polling
    }
    await sleep(intervalMs);
  }
  return null;
};

const invalidateLibraryQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  {
    operatorId = null,
    waitForIndex = true,
    expectRelativePath = null,
    expectAbsentPath = null,
  }: {
    operatorId?: number | null;
    waitForIndex?: boolean;
    expectRelativePath?: string | null;
    expectAbsentPath?: string | null;
  } = {},
) => {
  if (waitForIndex) {
    await waitForLibraryIndexIdle({
      queryClient,
      operatorId,
      expectRelativePath,
      expectAbsentPath,
    });
  }
  await Promise.all([
    queryClient.invalidateQueries(["library-tree", operatorQueryKey(operatorId)]),
    queryClient.invalidateQueries(["library-folder-links", operatorQueryKey(operatorId)]),
    queryClient.invalidateQueries(["library-compliance", operatorQueryKey(operatorId)]),
    queryClient.invalidateQueries(["library-versions", operatorQueryKey(operatorId)]),
  ]);
};

const queueLibraryQueryInvalidation = (
  queryClient: ReturnType<typeof useQueryClient>,
  options: Parameters<typeof invalidateLibraryQueries>[1],
) => {
  void invalidateLibraryQueries(queryClient, options).catch(() => {
    // A later focus/navigation refetch is still available if revalidation fails.
  });
};

const buildOptimisticDirectoryNode = (relativePath: string): FileExplorerNode => {
  const name = basenameRelativePath(relativePath);
  return {
    id: `optimistic:${relativePath}`,
    name,
    path: null,
    relativePath,
    kind: "directory",
    sourceKind: "library",
    hasChildren: false,
    childCount: 0,
    flightId: null,
    flightIdentifier: null,
    registration: null,
    departureAirport: null,
    arrivalAirport: null,
    scheduledDeparture: null,
    documentDate: null,
    routeCode: null,
    docTypeKey: null,
    docTypeLabel: null,
    flags: [],
    lastModified: null,
    extension: null,
    sizeBytes: null,
    previewPath: null,
    downloadPath: null,
    flightPath: null,
  };
};

const buildOptimisticFileNode = (relativePath: string, file: File): FileExplorerNode => {
  const name = basenameRelativePath(relativePath);
  const extension = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : null;
  return {
    ...buildOptimisticDirectoryNode(relativePath),
    id: `optimistic:${relativePath}`,
    name,
    kind: "file",
    sizeBytes: file.size,
    extension,
  };
};

const upsertOptimisticTreeNode = (
  queryClient: ReturnType<typeof useQueryClient>,
  relativePath: string,
  node: FileExplorerNode,
  operatorId?: number | null,
) => {
  const parentDavPath = resolveParentDavPathFromCache(queryClient, relativePath, operatorId);
  // Root listing uses ""; unknown parent → skip precise upsert (invalidate will fix).
  if (parentDavPath === null) {
    return;
  }
  queryClient.setQueryData<LibraryTreeResponse | undefined>(
    libraryTreeQueryKey(operatorId, parentDavPath),
    (current) => {
      if (!current) {
        return {
          indexedAt: null,
          isRefreshing: true,
          refreshStatus: {
            stage: "starting",
            indexedCount: 0,
            totalEstimate: 0,
            progressPercent: null,
            startedAt: null,
            isRefreshing: true,
          },
          nodes: [node],
          folderLinks: [],
        };
      }
      const withoutDup = (current.nodes || []).filter(
        (entry) =>
          entry.relativePath !== relativePath &&
          !(entry.kind === node.kind && entry.name === node.name),
      );
      const nodes = [...withoutDup, node].sort((a, b) => {
        if (a.kind !== b.kind) {
          return a.kind === "directory" ? -1 : 1;
        }
        return `${a.name}`.localeCompare(`${b.name}`, undefined, { sensitivity: "base" });
      });
      return { ...current, isRefreshing: true, nodes };
    },
  );
};

const optimisticallyRelocateTreeNode = (
  queryClient: ReturnType<typeof useQueryClient>,
  fromPath: string,
  toPath: string,
  operatorId?: number | null,
) => {
  const movedNode = findNodeInTreeCaches(queryClient, fromPath, operatorId);
  patchTreeCachesForRelativePath(queryClient, fromPath, operatorId, (nodes) =>
    nodes.filter(
      (entry) =>
        entry.relativePath !== fromPath &&
        !`${entry.relativePath || ""}`.startsWith(`${fromPath}/`),
    ),
  );
  if (!movedNode) return;
  upsertOptimisticTreeNode(
    queryClient,
    toPath,
    {
      ...movedNode,
      id: `optimistic:${toPath}`,
      name: basenameRelativePath(toPath),
      relativePath: toPath,
      path: null,
    },
    operatorId,
  );
};

export const useLibraryTree = (
  path?: string | null,
  enabled = true,
  operatorId?: number | null,
) => {
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useQuery(
    libraryTreeQueryKey(normalizedOperatorId, path || ""),
    () => fetchLibraryTree(path, normalizedOperatorId),
    {
      enabled: Boolean(enabled && (normalizedOperatorId || operatorId === undefined)),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useLibraryAircraftOptions = (
  enabled = true,
  operatorId?: number | null,
) => {
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useQuery(
    ["library-aircraft-options", operatorQueryKey(normalizedOperatorId)],
    () =>
      fetchJson<LibraryAircraftOptions>(buildLibraryAircraftOptionsUrl(normalizedOperatorId)),
    {
      enabled: Boolean(enabled && (normalizedOperatorId || operatorId === undefined)),
      staleTime: 60_000,
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useLibraryFolderLinks = (enabled = true, operatorId?: number | null) => {
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  const searchParams = withOperatorParam(new URLSearchParams(), normalizedOperatorId);
  return useQuery(
    ["library-folder-links", operatorQueryKey(normalizedOperatorId)],
    () =>
      fetchJson<{ operatorRef: number; links: LibraryFolderLink[] }>(
        `${API_URL}/library/folder-links${
          searchParams.toString() ? `?${searchParams.toString()}` : ""
        }`,
      ),
    {
      enabled: Boolean(enabled && (normalizedOperatorId || operatorId === undefined)),
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useLibraryCompliance = (
  params: {
    userId?: string | null;
    userQuery?: string;
    fileQuery?: string;
    folder?: string | null;
    aircraft?: string | null;
    status?: string | null;
    includeUserLists?: boolean;
    operatorId?: number | null;
  } = {},
  enabled = true,
) => {
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(params.operatorId);
  const searchParams = withOperatorParam(new URLSearchParams(), normalizedOperatorId);
  if (params.userId) searchParams.set("userId", params.userId);
  if (params.userQuery) searchParams.set("userQuery", params.userQuery);
  if (params.fileQuery) searchParams.set("fileQuery", params.fileQuery);
  if (params.folder) searchParams.set("folder", params.folder);
  if (params.aircraft) searchParams.set("aircraft", params.aircraft);
  if (params.status) searchParams.set("status", params.status);
  if (params.includeUserLists === false) searchParams.set("includeUserLists", "0");

  return useQuery(
    [
      "library-compliance",
      operatorQueryKey(normalizedOperatorId),
      params.userId || "",
      params.userQuery || "",
      params.fileQuery || "",
      params.folder || "",
      params.aircraft || "",
      params.status || "",
      params.includeUserLists === false ? "slim" : "full",
    ],
    () =>
      fetchJson<LibraryComplianceResponse>(
        `${API_URL}/library/compliance${searchParams.toString() ? `?${searchParams}` : ""}`,
      ),
    {
      enabled: Boolean(enabled && (normalizedOperatorId || params.operatorId === undefined)),
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useLibraryVersions = (
  path?: string | null,
  enabled = true,
  operatorId?: number | null,
) => {
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  const searchParams = withOperatorParam(new URLSearchParams(), normalizedOperatorId);
  if (path) {
    searchParams.set("path", path);
  }
  return useQuery(
    ["library-versions", operatorQueryKey(normalizedOperatorId), path || ""],
    () =>
      fetchJson<{ path: string | null; versions: Array<Record<string, unknown>> }>(
        `${API_URL}/library/versions?${searchParams.toString()}`,
      ),
    {
      enabled: Boolean(enabled && path && (normalizedOperatorId || operatorId === undefined)),
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useRefreshLibrary = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    () =>
      fetchJson(`${API_URL}/library/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({}, normalizedOperatorId)),
      }),
    {
      onSuccess: () => {
        queueLibraryQueryInvalidation(queryClient, { operatorId: normalizedOperatorId });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useScanLibrary = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    () =>
      fetchJson<LibraryScanDiff>(`${API_URL}/library/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({}, normalizedOperatorId)),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["library-versions"]);
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useCreateLibraryFolder = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    (path: string) =>
      fetchJson<{ path: string; davPath: string }>(`${API_URL}/library/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({ path }, normalizedOperatorId)),
      }),
    {
      onSuccess: (result, path) => {
        const relativePath = result?.path || path;
        upsertOptimisticTreeNode(
          queryClient,
          relativePath,
          buildOptimisticDirectoryNode(relativePath),
          normalizedOperatorId,
        );
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          expectRelativePath: relativePath,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useMoveLibraryPath = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    ({ fromPath, toPath }: { fromPath: string; toPath: string }) =>
      fetchJson(`${API_URL}/library/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({ fromPath, toPath }, normalizedOperatorId)),
      }),
    {
      onSuccess: (_result, variables) => {
        optimisticallyRelocateTreeNode(
          queryClient,
          variables.fromPath,
          variables.toPath,
          normalizedOperatorId,
        );
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          expectRelativePath: variables.toPath,
          expectAbsentPath: variables.fromPath,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useRenameLibraryPath = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    ({ path, newName }: { path: string; newName: string }) =>
      fetchJson<{ fromPath?: string; toPath?: string }>(`${API_URL}/library/rename`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({ path, newName }, normalizedOperatorId)),
      }),
    {
      onSuccess: (result, variables) => {
        const toPath =
          result?.toPath ||
          (() => {
            const parent = parentRelativePath(variables.path);
            return parent ? `${parent}/${variables.newName}` : variables.newName;
          })();
        optimisticallyRelocateTreeNode(
          queryClient,
          variables.path,
          toPath,
          normalizedOperatorId,
        );
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          expectRelativePath: toPath,
          expectAbsentPath: variables.path,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useDeleteLibraryPath = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    (path: string) =>
      fetchJson(`${API_URL}/library/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOperatorBody({ path }, normalizedOperatorId)),
      }),
    {
      onSuccess: (_result, path) => {
        patchTreeCachesForRelativePath(
          queryClient,
          path,
          normalizedOperatorId,
          (nodes) =>
            nodes.filter(
              (entry) =>
                entry.relativePath !== path &&
                !`${entry.relativePath || ""}`.startsWith(`${path}/`),
            ),
        );
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          expectAbsentPath: path,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useUploadLibraryFiles = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    async ({ files, path }: { files: File[]; path?: string | null }) => {
      const formData = new FormData();
      if (path?.trim()) {
        formData.set("path", path.trim());
      }
      if (normalizedOperatorId) {
        formData.set("operatorId", `${normalizedOperatorId}`);
      }
      files.forEach((file) => formData.append("files", file));
      const res = await fetch(`${API_URL}/library/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        const text = await res.text();
        const error = new Error(parseErrorMessage(text, `${res.status}`)) as HttpError;
        error.status = res.status;
        throw error;
      }
      return res.json();
    },
    {
      onSuccess: (_result, variables) => {
        const target = `${variables.path || ""}`.replace(/^\/+|\/+$/g, "");
        variables.files.forEach((file) => {
          const relativePath = target ? `${target}/${file.name}` : file.name;
          upsertOptimisticTreeNode(
            queryClient,
            relativePath,
            buildOptimisticFileNode(relativePath, file),
            normalizedOperatorId,
          );
        });
        const firstFile = variables.files[0];
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          expectRelativePath: firstFile
            ? target
              ? `${target}/${firstFile.name}`
              : firstFile.name
            : null,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const usePutLibraryFolderLinks = (operatorId?: number | null) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizedOperatorId = normalizeOperatorId(operatorId);
  return useMutation(
    ({
      folderRelativePath,
      links,
    }: {
      folderRelativePath: string;
      links: Array<{ kind: string; value: string }>;
    }) =>
      fetchJson(`${API_URL}/library/folder-links`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          withOperatorBody({ folderRelativePath, links }, normalizedOperatorId),
        ),
      }),
    {
      onSuccess: () => {
        queueLibraryQueryInvalidation(queryClient, {
          operatorId: normalizedOperatorId,
          waitForIndex: false,
        });
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};

export const useBulkAssignUserAircraft = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation(
    (body: {
      operatorRef: number;
      userIds: Array<string | number>;
      assignments: UserAircraftAssignment[];
      mode?: "add" | "replace";
    }) =>
      fetchJson(`${API_URL}/users/aircraft-assignments/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("users");
        queryClient.invalidateQueries("library-compliance");
      },
      onError: (error: Error) => redirectUnauthorized(navigate, error),
    },
  );
};
